import type { ReactNode } from 'react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import * as S from './Bar.styled'

export type BarProps = {
  value: number
  maxValue: number
  color?: string
  formatValue?: (value: number) => ReactNode
  animation?: boolean
}

export const Bar = React.memo(
  ({ value, maxValue = 100, color, formatValue, animation = true }: BarProps) => {
    const [isAnimated, setIsAnimated] = useState(false)
    const [isValueAnimated, setIsValueAnimated] = useState(false)
    const animationRef = useRef<number>()
    const isInitialMount = useRef(true)

    const targetWidth = useMemo(
      () => Number(((value / maxValue) * 100).toFixed(2)),
      [value, maxValue],
    )

    const displayValue = useCallback(
      (value: number) => (formatValue ? formatValue(value) : value),
      [formatValue],
    )

    useEffect(() => {
      if (!animation) {
        setIsAnimated(true)
        setIsValueAnimated(true)

        return
      }

      // First render - animate from 0
      if (isInitialMount.current) {
        isInitialMount.current = false
        setIsAnimated(false)
        setIsValueAnimated(false)

        // Use requestAnimationFrame for better performance
        animationRef.current = requestAnimationFrame(() => {
          setIsAnimated(true)
          setIsValueAnimated(true)
        })
      } else {
        // Subsequent renders - set directly
        setIsAnimated(true)
        setIsValueAnimated(true)
      }

      return () => {
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current)
        }
      }
    }, [targetWidth, animation])

    return (
      <S.Container>
        <S.Bar
          $width={targetWidth}
          $color={color}
          $animation={animation}
          $isAnimated={isAnimated}
        />
        <S.Value $animation={animation} $isAnimated={isValueAnimated}>
          {displayValue(value)}
        </S.Value>
      </S.Container>
    )
  },
)
